// #1167/#1168 RU bid-write modal — slim 3-field shape with add + edit modes.
//
// Fields: price (number, required) + comment (optional) + validityDays
// (positive int, default 7).
//
// @aiPurpose drives `POST /russia-requests/:id/carriers/:carrierId/bids`
//   (add mode) or `PATCH .../bids/:bidId` (edit mode, when `bid` is
//   supplied). The server computes `expiresAt`/`isExpired` from
//   `validityDays + now()`; on edit, the server also refreshes
//   `quotedAt = now()` so the window restarts.

import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorCode, type RussiaRequestCarrierBid } from '@magimp/shared';

import { ApiClientError } from '../../../../api/errors.js';
import {
  Button,
  EditorModal,
  FormRow,
  Input,
  Textarea,
  useEditorGuard,
} from '../../../../ui/index.js';
import { useAddRussiaRequestBid, useUpdateRussiaRequestBid } from '../../queries.js';

type BidErrorKey =
  | 'drawer.carriers.bid.invalidStatus'
  | 'drawer.carriers.bid.notFound'
  | 'drawer.carriers.bid.generic';

function bidErrorKey(err: unknown): BidErrorKey {
  if (err instanceof ApiClientError) {
    if (err.code === ErrorCode.INVALID_TRANSITION) return 'drawer.carriers.bid.invalidStatus';
    if (err.code === ErrorCode.NOT_FOUND) return 'drawer.carriers.bid.notFound';
  }
  return 'drawer.carriers.bid.generic';
}

const DEFAULT_VALIDITY_DAYS = 7;

export interface RussiaCarriersBidFormModalProps {
  russiaRequestId: string;
  russiaRequestCarrierId: string;
  carrierName: string;
  /** When present, the modal is in edit mode and submit calls PATCH. */
  bid?: RussiaRequestCarrierBid;
  /**
   * #1580 — when present, the ADD submit creates a per-departure-city
   * (`START_CITY`) bid scoped to this `arrivalCityId`. Absent ⇒ the legacy
   * whole-request (`REQUEST`) bid. PATCH never changes scope (the server
   * keeps scope immutable on update), so this is ignored in edit mode.
   */
  startCityRefId?: string;
  onClose: () => void;
}

export function RussiaCarriersBidFormModal({
  russiaRequestId,
  russiaRequestCarrierId,
  carrierName,
  bid,
  startCityRefId,
  onClose,
}: RussiaCarriersBidFormModalProps) {
  const { t } = useTranslation(['russiaRequests', 'common']);
  const addMutation = useAddRussiaRequestBid(russiaRequestId);
  const updateMutation = useUpdateRussiaRequestBid(russiaRequestId);
  const mutation = bid ? updateMutation : addMutation;
  const initialPrice = bid ? String(bid.price) : '';
  const initialComment = bid ? bid.comment : '';
  const initialValidity = bid ? String(bid.validityDays) : String(DEFAULT_VALIDITY_DAYS);
  const [price, setPrice] = useState(initialPrice);
  const [comment, setComment] = useState(initialComment);
  const [validityDays, setValidityDays] = useState(initialValidity);
  // INVARIANT (#479): pin the dirty baseline at first render — a
  //   background refetch must not slide it.
  const baselineRef = useRef({
    price: initialPrice,
    comment: initialComment,
    validityDays: initialValidity,
  });

  const normalizedPrice = Number(price.replace(',', '.'));
  const parsedValidity = Number(validityDays);
  const priceValid = Number.isFinite(normalizedPrice) && normalizedPrice > 0;
  // INVARIANT (#1167 F-003): mirror `AddRussiaRequestBidBodySchema`'s
  //   `validityDays.min(1).max(3650)` — submit must not enable for
  //   values the server will reject.
  const validityValid =
    Number.isInteger(parsedValidity) && parsedValidity >= 1 && parsedValidity <= 3650;
  const canSubmit = priceValid && validityValid && !mutation.isPending;

  async function onSubmit(): Promise<void> {
    if (!canSubmit) return;
    try {
      if (bid) {
        await updateMutation.mutateAsync({
          russiaRequestCarrierId,
          bidId: bid.id,
          input: {
            price: normalizedPrice,
            comment: comment.trim(),
            validityDays: parsedValidity,
          },
        });
      } else {
        await addMutation.mutateAsync({
          russiaRequestCarrierId,
          // WHY (#1580): a per-city modal submits the START_CITY scope +
          //   the start-city arrivalCityId; otherwise the legacy REQUEST
          //   bid (scope defaults server-side, scopeRefId stays absent).
          input: startCityRefId
            ? {
                price: normalizedPrice,
                comment: comment.trim(),
                validityDays: parsedValidity,
                scope: 'START_CITY',
                scopeRefId: startCityRefId,
              }
            : {
                price: normalizedPrice,
                comment: comment.trim(),
                validityDays: parsedValidity,
              },
        });
      }
      onClose();
    } catch {
      // WHY: error surface reads `mutation.error`.
    }
  }

  const baseline = baselineRef.current;
  const dirty =
    price.trim() !== baseline.price.trim() ||
    comment.trim() !== baseline.comment.trim() ||
    validityDays.trim() !== baseline.validityDays.trim();

  const title = bid
    ? t('drawer.carriers.editBid.title')
    : t('drawer.carriers.bidModal.titlePrimary', { name: carrierName });
  const testidSuffix = russiaRequestCarrierId;

  return (
    <EditorModal
      open
      dirty={dirty}
      busy={mutation.isPending}
      onConfirmClose={onClose}
      title={title}
      width={420}
    >
      <div
        data-testid={`russia-request-drawer-carrier-bid-modal-${testidSuffix}`}
        style={{ display: 'grid', gap: 10 }}
      >
        <div data-testid={`russia-request-drawer-carrier-bid-price-${testidSuffix}`}>
          <FormRow label={t('drawer.carriers.bid.priceLabel')}>
            <Input
              value={price}
              onChange={setPrice}
              ariaLabel={t('drawer.carriers.bid.priceLabel')}
              invalid={price.length > 0 && !priceValid}
            />
          </FormRow>
        </div>
        <div data-testid={`russia-request-drawer-carrier-bid-validity-${testidSuffix}`}>
          <FormRow label={t('drawer.carriers.bid.validityDaysLabel')}>
            <Input
              value={validityDays}
              onChange={setValidityDays}
              ariaLabel={t('drawer.carriers.bid.validityDaysLabel')}
              invalid={validityDays.length > 0 && !validityValid}
            />
          </FormRow>
        </div>
        <div data-testid={`russia-request-drawer-carrier-bid-comment-${testidSuffix}`}>
          <FormRow label={t('drawer.carriers.bid.commentLabel')}>
            <Textarea
              value={comment}
              onChange={setComment}
              rows={3}
              ariaLabel={t('drawer.carriers.bid.commentLabel')}
            />
          </FormRow>
        </div>
        {mutation.isError && (
          <span
            role="alert"
            data-testid={`russia-request-drawer-carrier-bid-error-${testidSuffix}`}
            style={{ fontSize: 12, color: 'var(--status-error-fg)' }}
          >
            {t(bidErrorKey(mutation.error))}
          </span>
        )}
        <BidModalFooter
          onClose={onClose}
          onSubmit={onSubmit}
          canSubmit={canSubmit}
          submitTestId={`russia-request-drawer-carrier-bid-submit-${testidSuffix}`}
        />
      </div>
    </EditorModal>
  );
}

function BidModalFooter({
  onClose,
  onSubmit,
  canSubmit,
  submitTestId,
}: {
  onClose: () => void;
  onSubmit: () => void | Promise<void>;
  canSubmit: boolean;
  submitTestId: string;
}) {
  const { t } = useTranslation(['russiaRequests', 'common']);
  const { confirmDestructive } = useEditorGuard();
  return (
    <div
      style={{
        marginTop: 6,
        marginLeft: -16,
        marginRight: -16,
        marginBottom: -16,
        padding: '12px 16px',
        borderTop: '1px solid var(--border-2)',
        display: 'flex',
        gap: 8,
        justifyContent: 'flex-end',
      }}
    >
      <Button kind="ghost" onClick={() => confirmDestructive(onClose)}>
        {t('common:actions.cancel')}
      </Button>
      <Button
        kind="primary"
        disabled={!canSubmit}
        onClick={() => {
          void onSubmit();
        }}
        data-testid={submitTestId}
      >
        {t('drawer.carriers.bid.submit')}
      </Button>
    </div>
  );
}
